import type React from 'react';

import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FaceDetector } from '@vlabs/monokit/shared/ui/face-detector/FaceDetector';

import st from './CardFaceInput.module.sass';

interface CardFaceInputProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
}

function CardFaceInput<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
}: CardFaceInputProps<TFieldValues>): React.JSX.Element {
  const { t } = useTranslation();
  const { field, fieldState: { error } } = useController<TFieldValues>({
    name,
    control,
    rules: {
      validate: {
        required: (value) => {
          if (value === null) return t('features:createCard.selected_image_does_not_contain_faces');
          return true;
        },
      },
    },
  });

  return (
    <div className={st.Root}>
      <FaceDetector
        dropzoneClassName={st.Dropzone}
        onChange={(selectedSamples) => {
          if (Array.isArray(selectedSamples)) {
            field.onChange(selectedSamples.length === 0 ? null : selectedSamples?.[0]?.sample_id);
          } else {
            field.onChange(undefined);
          }
        }}
      />
      {!!error?.message && (
        <span className={st.Error}>
          {error?.message}
        </span>
      )}
    </div>
  );
}

export { CardFaceInput };
